import { mantineThemeColors } from '@dealstack/frontend/shared/mechanism/styleguide/mantineThemeColors.ts';
import { v3Colors } from '@dealstack/shared/lib/frontend/styled-components/styled';
import { DefaultTheme } from 'styled-components';

export const defaultTheme: DefaultTheme = {
	shadow: {
		z0: 'box-shadow: 0px 1px 2px rgba(0, 48, 69, 0.12);',
		z1: 'box-shadow: 0px 12px 24px rgba(0, 48, 69, 0.08), 0px 2px 8px rgba(0, 48, 69, 0.04);',
		z2: 'box-shadow: 0px 2px 4px rgba(0, 48, 69, 0.12), 0px 4px 8px rgba(0, 48, 69, 0.12);',
		z3: 'box-shadow: 0px 8px 16px rgba(0, 48, 69, 0.04), 0px 4px 4px rgba(0, 48, 69, 0.04);',
		z4: 'box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(233, 245, 0, 0.08), 0px 2px 4px rgba(0, 48, 69, 0.08);',
		innerBottom: 'box-shadow: inset 0 -2.56rem 3rem -4.43rem rgba(66, 68, 90, 1);',
		innerBottomLight: 'box-shadow: inset 0 -1.8rem 3rem -4.43rem rgba(66, 68, 90, 1);',
		innerTop: 'box-shadow: inset 0 2.56rem 4rem -6.33rem rgb(66 68 90)',
	},
	v3: {
		colors: mantineThemeColors as unknown as v3Colors,
	},
	v2: {
		color: {
			black: {
				500: '#000000',
			},
			teal: {
				800: '#00A36D',
			},
			altHigh: {
				red: '#49040D',
				orange: '#491604',
				peach: '#493B04',
				yellow: '#492904',
				neon: '#444904',
				green: '#04490D',
				aqua: '#044932',
				teal: '#043B49',
				blue: '#041C49',
				violet: '#160449',
				purple: '#3B0449',
				lilac: '#490432',
			},
			altLow: {
				red: '#FBB6C0',
				orange: '#FBC9B6',
				peach: '#FBEDB6',
				yellow: '#FBDBB6',
				yellow_new: '#FFF9AB',
				neon: '#F7FBB6',
				green: '#B6FBC0',
				aqua: '#B6EDFB',
				teal: '#B6FBE4',
				blue: '#B6CEFB',
				violet: '#C9B6FB',
				purple: '#EDB6FB',
				lilac: '#FBB6E4',
			},
			alternateMid: {
				red: '#C20A23',
				orange: '#C23B0A',
				peach: '#C29D0A',
				yellow: '#C26C0A',
				neon: '#B6C20A',
				green: '#0AC223',
				aqua: '#0A9DC2',
				teal: '#0AC285',
				blue: '#0A4AC2',
				violet: '#3B0AC2',
				purple: '#9D0AC2',
				lilac: '#C20A85',
			},
			neutral: {
				1200: '#070C0E',
				1100: '#0E171C',
				1000: '#15232B',
				900: '#1C2E39',
				800: '#233A47',
				700: '#4F616C',
				600: '#7B8991',
				500: '#A7B0B5',
				400: '#BDC4C8',
				300: '#D3D8DA',
				200: '#E9EBED',
				100: '#F4F5F6',
			},
			warning: {
				1200: '#492904',
				1000: '#C26C0A',
				800: '#F2870D',
				600: '#F7B76E',
				400: '#FBDBB6',
				200: '#FEF3E7',
			},
			error: {
				1200: '#49040D',
				1000: '#C20A23',
				800: '#F20D2B',
				600: '#F76E80',
				400: '#FBB6C0',
				200: '#FEE7EA',
			},
			success: {
				1200: '#04490D',
				1000: '#0AC223',
				800: '#0DF22B',
				600: '#6EF780',
				400: '#B6FBC0',
				200: '#E7FEEA',
			},
			utilityGreen: {
				1200: '#0A1A0A',
				1000: '#1D4D1D',
				800: '#308030',
				600: '#83B383',
				400: '#C1D9C1',
				200: '#EAF2EA',
			},
			secondary: {
				1200: '#071933',
				1000: '#0A4AC2',
				800: '#0D5DF2',
				600: '#7CB2FF',
				400: '#B6CEFB',
				200: '#E7EFFE',
			},
			primary: {
				1200: '#282E0E',
				1000: '#778B2B',
				800: '#C7E747',
				600: '#DDF191',
				400: '#EEF8C8',
				200: '#F9FDED',
			},
			tertiary: {
				1200: '#210F33',
				1000: '#622C99',
				800: '#A44AFF',
				600: '#C892FF',
				400: '#E4C9FF',
				200: '#F6EDFF',
			},
			white: {
				500: '#FFFFFF',
			},
			gradients: {
				linear: 'background: linear-gradient(180deg, #F3F4F5 0%, #E6E9EB 100%);',
				radial: 'background: radial-gradient(100% 256% at 100% 100%, #E6E9EB 0%, #F3F4F5 100%);',
				radialLight:
					'background: radial-gradient(100% 256% at 0% 0%, #F6F9FB 0%, #EBF0F3 100%);',
			},
			overlays: {
				grey: '#0E171C',
			},
		},
	},
	color: {
		yellow: {
			600: '#C7E747',
			500: '#DDF191',
			400: '#EEF8C8',
		},
		blue: {
			800: '#041C49',
			700: '#0A4AC2',
			600: '#0D5DF2',
			500: '#6E9EF7',
			50: '#B6CEFB',
			10: '#E7EFFE',
		},
		success: {
			700: '#04490D',
			500: '#0AC223',
			400: '#0DF22B',
			50: '#6EF780',
		},
		warning: {
			700: '#492904',
			600: '#C26C0A',
			500: '#F2870D',
			50: '#F7B76E',
		},
		error: {
			700: '#49040D',
			600: '#C20A23',
			500: '#F20D2B',
			50: '#F76E80',
		},
		greys: {
			700: '#0E171C',
			600: '#4F616C',
			500: '#7B8991',
			400: '#A7B0B5',
			300: '#BDC4C8',
			200: '#D3D8DA',
			100: '#E9EBED',
			50: '#F4F5F6',
		},
		white: {
			500: '#FFFFFF',
		},
		gradients: {
			linear: 'background: linear-gradient(180deg, #F3F4F5 0%, #E6E9EB 100%);',
			radial: 'background: radial-gradient(100% 256% at 100% 100%, #E6E9EB 0%, #F3F4F5 100%);',
			radialLight:
				'background: radial-gradient(100% 256% at 0% 0%, #F6F9FB 0%, #EBF0F3 100%);',
		},
		overlays: {
			grey: '#0E171C',
		},
		shadow: {
			grey: '#dcdfe0',
		},
	},
};
